/** @jsx jsx */
import React, { useRef, type ReactNode } from 'react';
import { Stack } from '@atlaskit/primitives/compiled';
import CrossIcon from '@atlaskit/icon/core/cross';
import { cssMap, jsx } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useFocusFirstInteractiveElement } from '@atlassian/jira-business-common/src/common/utils/use-focus-first-interactive-element/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	wrapper: {
		height: '100%',
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingBlock: token('space.200'),
		paddingInline: token('space.300'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		position: 'sticky',
		top: 0,
		backgroundColor: token('elevation.surface'),
		zIndex: 1,
	},
	body: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		paddingBlockEnd: token('space.200'),
		paddingInline: token('space.300'),
		gap: token('space.200'),
	},
	footer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.100'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.300'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		position: 'sticky',
		bottom: 0,
		backgroundColor: token('elevation.surface'),
	},
});

type Props = {
	header: ReactNode;
	footer?: ReactNode;
	children?: ReactNode;
	onClose: () => void;
};

export const ViewSettingsPanelBase = ({ header, children, footer, onClose }: Props) => {
	const { formatMessage } = useIntl();
	const containerRef = useRef<HTMLDivElement>(null);

	useFocusFirstInteractiveElement(containerRef);

	return (
		<Stack space="space.200" xcss={styles.wrapper} ref={containerRef}>
			<div css={styles.header}>
				{header}
				<IconButton
					icon={CrossIcon}
					label={formatMessage(messages.closeButtonLabel)}
					onClick={onClose}
					isTooltipDisabled={false}
				/>
			</div>
			<div css={styles.body}>{children}</div>
			{footer && <div css={styles.footer}>{footer}</div>}
		</Stack>
	);
};
